
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Activity, ActivityFormValues } from "@/components/activities/schema/activitySchema";

// Fetch all activities for the current user
export const useActivities = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["activities"],
    queryFn: async () => {
      if (!user) {
        throw new Error("Utilisateur non authentifié");
      }

      const { data, error } = await supabase
        .from("activities" as any)
        .select("*")
        .eq("user_id", user.id)
        .order("scheduled_for", { ascending: true });

      if (error) throw error;
      return data as unknown as Activity[];
    },
    enabled: !!user,
  });
};

// Fetch a single activity by ID
export const useActivity = (id: string) => {
  return useQuery({
    queryKey: ["activities", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("activities" as any)
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as unknown as Activity;
    },
    enabled: !!id,
  });
};

// Create a new activity
export const useCreateActivity = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (newActivity: ActivityFormValues) => {
      if (!user) {
        throw new Error("Utilisateur non authentifié");
      }

      // Combine date and time into a full datetime string (ISO format)
      const scheduledDate = new Date(newActivity.scheduled_for);
      const [hours, minutes] = newActivity.time.split(':').map(Number);
      scheduledDate.setHours(hours, minutes);

      // Create a copy of the activity data and remove the 'time' field
      // since it doesn't exist in the database schema
      const { time, ...activityWithoutTime } = newActivity;

      const { data, error } = await supabase
        .from("activities" as any)
        .insert([
          {
            ...activityWithoutTime,
            user_id: user.id,
            scheduled_for: scheduledDate.toISOString(),
          } as any,
        ])
        .select()
        .single();

      if (error) throw error;
      return data as unknown as Activity;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      toast.success("Activité planifiée avec succès");
    },
    onError: (error: any) => {
      toast.error(`Erreur lors de la création de l'activité: ${error.message}`);
    },
  });
};

// Update an existing activity
export const useUpdateActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (activity: Partial<Activity> & { id: string }) => {
      const { id, time, ...updates } = activity;

      // If updates contain scheduled_for date and time fields, combine them
      if (updates.scheduled_for && time) {
        const scheduledDate = new Date(updates.scheduled_for);
        const [hours, minutes] = time.split(':').map(Number);
        scheduledDate.setHours(hours, minutes);
        updates.scheduled_for = scheduledDate;
      }

      const { data, error } = await supabase
        .from("activities" as any)
        .update(updates as any)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as Activity;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["activities", variables.id] });
      toast.success("Activité mise à jour avec succès");
    },
    onError: (error: any) => {
      toast.error(`Erreur lors de la mise à jour de l'activité: ${error.message}`);
    },
  });
};

// Delete an activity
export const useDeleteActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("activities" as any)
        .delete()
        .eq("id", id);

      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["activities", id] });
      toast.success("Activité supprimée avec succès");
    },
    onError: (error: any) => {
      toast.error(`Erreur lors de la suppression de l'activité: ${error.message}`);
    },
  });
};
